/**
 * useUpdateShipment - Hook para actualizar shipments
 * 
 * Permite actualizar un shipment específico enviando peso, número de items y líneas.
 * Realiza PUT a /invoices/shipments/{id} con los datos editados.
 * Muestra toasts de éxito/error y ejecuta callback de refetch al completar exitosamente.
 * 
 * @returns {Object} { updateShipment: función de actualización, isUpdating: estado de carga }
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { ShipmentsApiAdapter } from '../adapters/ShipmentsApiAdapter';
import { apiClientWH } from '@shared/services/ApiClient';
import { createHttpClient } from '@adapters/HttpClient';

export interface UpdateShipmentPayload {
  weight_internal_kg?: number;
  num_items?: number;
  lines?: Array<{
    id: number;
    base_amount: number;
  }>;
}

export const useUpdateShipment = () => {
  const { t } = useTranslation('invoicesOrdersModule');
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);

  const updateShipment = useCallback(
    async (
      shipmentId: number,
      payload: UpdateShipmentPayload,
      onSuccess?: () => void
    ) => {
      setIsUpdating(true);

      try {
        const shipmentsAdapter = new ShipmentsApiAdapter(
          createHttpClient(apiClientWH)
        );
        await shipmentsAdapter.updateShipment(shipmentId, payload);

        // 🎯 Si llegamos aquí, la API devolvió 200 OK = éxito
        toast({
          title: t('toast.update_success.title', 'Shipment actualizado'),
          description: t('toast.update_success.description', 'El shipment se ha actualizado exitosamente'),
          variant: 'default',
        });

        // 🎯 Ejecutar callback de éxito (refetch de datos)
        onSuccess?.();
      } catch (error: any) {
        // 🎯 Extraer mensaje amigable de la API
        const apiMessage: string | undefined = error?.response?.data?.message;
        const apiErrors: Record<string, string[] | string> | undefined =
          error?.response?.data?.errors;
        const statusCode = error?.response?.status;

        let description = apiMessage;

        // Manejar códigos de error específicos
        if (statusCode === 404) {
          description = t('toast.update_error.not_found', 'El shipment no fue encontrado');
        } else if (statusCode === 422) {
          description = t('toast.update_error.already_invoiced', 'El shipment ya está facturado y no se puede modificar');
        }

        // Unir los errores detallados si existen
        if (apiErrors && typeof apiErrors === 'object') {
          const details = Object.values(apiErrors)
            .flat()
            .filter(Boolean)
            .join(' • ');
          description = description ? `${description} — ${details}` : details;
        }

        // Fallback de mensaje
        if (!description) {
          description =
            error?.message ||
            t(
              'toast.update_error.unexpected',
              'Error inesperado al actualizar el shipment. Inténtalo de nuevo.'
            );
        }

        toast({
          title: t('toast.update_error.title', 'Error al actualizar'),
          description,
          variant: 'destructive',
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [t, toast]
  );

  return {
    updateShipment,
    isUpdating,
  };
};

